import { useTranslation } from 'react-i18next';
import { AccountData, AccountLocationData, LocationsStatus, OrderPageData } from '../api';
import { useLanguage } from '../common/GeneralUtils';

export type OrderPageLocationGroup = {
  location: AccountLocationData | null;
  pages: OrderPageData[];
};

// Shared grouping used by the multi-select order-page components. Groups pages
// by their location_id under the account's active locations. Pages with no /
// unknown location fall into a leading "without default location" bucket so
// they're never hidden at the bottom.
export const useOrderPageLocationGroups = (account: AccountData) => {
  const { t } = useTranslation();
  const language = useLanguage();

  const activeLocations: AccountLocationData[] = (account.locations || []).filter(
    (l) => l.status === LocationsStatus.ACTIVE,
  );
  const groupByLocation = activeLocations.length > 1;

  const groupPagesByLocation = (pages: OrderPageData[]): OrderPageLocationGroup[] => {
    const knownIds = new Set(activeLocations.map((l) => l.id));
    const other = pages.filter((p) => !p.location_id || !knownIds.has(p.location_id));
    const groups: OrderPageLocationGroup[] = [];
    if (other.length > 0) groups.push({ location: null, pages: other });
    activeLocations.forEach((loc) => {
      const locPages = pages.filter((p) => p.location_id === loc.id);
      if (locPages.length > 0) groups.push({ location: loc, pages: locPages });
    });
    return groups;
  };

  const locationLabel = (loc: AccountLocationData | null) =>
    loc ? loc.name?.[language] || loc.address?.[language] || loc.id : t('locationPicker.withoutDefaultLocation');

  return { activeLocations, groupByLocation, groupPagesByLocation, locationLabel };
};
